import { Currency } from "@/components/Currency";
import {
  DescriptionList,
  DescriptionListItem,
} from "@/components/DescriptionList";
import { Heading } from "@/components/heading/Heading";
import { HeaderContainer } from "@/components/layout/HeaderContainer";
import { IconContainer } from "@/features/projects/cards/components/components";
import { getProjectValue } from "@/utils/getProjectValue";
import styled from "@emotion/styled";
import { Button, Text, Variables, arrowLeftIcon } from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import {
  CoWorkerName,
  CustomerModeToggle,
  FlexDiv,
  ProjectTitle,
  localizeDate,
  translateOpportunityType,
  translateProjectSource,
  useCustomer,
} from "@repo/ui";

import { useActiveProject } from "../ActiveProject";

const DescriptionListItemMobile = styled(DescriptionListItem)`
  @media (max-width: ${Variables.breakpointM}) {
    display: none;
  }
`;

const DescriptionListItemScreen = styled(DescriptionListItem)`
  @media (max-width: ${Variables.breakpointM}) {
    display: none;
  }
`;

const TitleText = styled(Heading)`
  width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderRow = styled(FlexDiv)`
  width: 100%;
  min-width: 0;
  align-items: center;
  flex-wrap: nowrap;
  overflow: hidden;
  justify-content: space-between;
  gap: ${Variables.space200};

  @media (max-width: ${Variables.breakpointL}) {
    gap: ${Variables.space100};
  }
`;

const MainInfo = styled(FlexDiv)`
  min-width: 0;
  overflow: hidden;
  flex-direction: column;
  flex-grow: 1;
`;

const AdditionalInfo = styled(DescriptionList)`
  display: flex;
  flex-direction: row;
  min-width: 0;
  flex-wrap: nowrap;
  overflow: hidden;
  gap: ${Variables.space150};
  justify-content: end;
  flex-grow: 1;

  @media (max-width: ${Variables.breakpointL}) {
    gap: ${Variables.space100};
  }
`;

const ToggleContainer = styled.div`
  flex-shrink: 0;
`;

export const ProjectHeader = () => {
  const { dateFnsLocale } = useTranslation();
  const { opportunity } = useActiveProject();
  const { customer } = useCustomer(opportunity?.id);

  return (
    <HeaderContainer>
      <HeaderRow>
        <IconContainer size={Variables.space250}>
          <Button
            ssrIcon={arrowLeftIcon}
            type="secondary"
            size="small"
            iconOnly
            title={t("action.back")}
            onClick={() => window.history.back()}
          />
        </IconContainer>

        <MainInfo>
          <TitleText textStyle="Heading.XS" noWrap>
            <ProjectTitle
              title={opportunity?.title || ""}
              customer={customer}
            />
          </TitleText>

          <Text>{opportunity?.id}</Text>
        </MainInfo>

        <AdditionalInfo>
          {opportunity?.type && (
            <DescriptionListItemMobile
              title={t("label.type")}
              description={translateOpportunityType(opportunity.type)}
            />
          )}

          {opportunity?.source && (
            <DescriptionListItemScreen
              title={t("label.source")}
              description={translateProjectSource(opportunity.source)}
            />
          )}
          {opportunity?.createdAt && (
            <DescriptionListItemScreen
              title={t("label.created")}
              description={localizeDate(opportunity.createdAt, dateFnsLocale)}
            />
          )}
          {opportunity?.updatedAt && (
            <DescriptionListItemMobile
              title={t("label.updated")}
              description={localizeDate(opportunity.updatedAt, dateFnsLocale)}
            />
          )}
          {opportunity?.customerMeetingPointBuName && (
            <DescriptionListItemScreen
              title={t("label.CMP_short")}
              description={opportunity.customerMeetingPointBuName}
            />
          )}

          {opportunity?.owner && (
            <DescriptionListItemScreen
              title={t("label.assigned")}
              description={<CoWorkerName email={opportunity.owner} />}
            />
          )}

          {opportunity && (
            <DescriptionListItemMobile
              title={t("label.value")}
              description={
                <Currency
                  value={getProjectValue(opportunity.displayAmount)}
                  currency={opportunity.currency}
                />
              }
            />
          )}
        </AdditionalInfo>

        <ToggleContainer>
          <CustomerModeToggle />
        </ToggleContainer>
      </HeaderRow>
    </HeaderContainer>
  );
};
